import cx from 'classnames';
import PropTypes from 'prop-types';

import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { Information, INFORMATION_SIZE } from 'src/components/Information';
import { UsageProgress } from 'src/components/UsageProgress';
import { customerInTrial, customerTrialEnded } from 'src/data/customer';
import { getCustomerBillingQueryQuota } from 'src/data/subscription';
import {
  getWordCreditsUsageTooltip,
  PERFORMANCE_PREDICTIONS_USAGE_TOOLTIP,
} from 'src/data/trialUsage';

import { useStyles } from './useStyles';

const Meter = ({ testId, title, tooltip, used, limit }) => {
  const styles = useStyles();
  const isLimited = limit ? used >= limit : false;

  return (
    <div
      className={styles.meter}
      data-testid={testId}
      data-limited={isLimited}
    >
      <div className={styles.header}>
        <CustomTypography
          className={styles.title}
          variant={TYPOGRAPHY_VARIANT.regular14}
        >
          {title}
        </CustomTypography>
        <div className={styles.countWrapper}>
          <CustomTypography
            className={styles.count}
            variant={TYPOGRAPHY_VARIANT.regular14}
          >
            <span data-testid={`${testId}_used`}>{used}</span>/
            <span data-testid={`${testId}_limit`}>{limit}</span>
          </CustomTypography>
          <Information
            className={styles.information}
            content={tooltip}
            size={INFORMATION_SIZE.small}
            placement="bottom"
          />
        </div>
      </div>
      <UsageProgress
        classes={{
          linearProgress: styles.linearProgress,
          bar: isLimited ? styles.limitBar : styles.bar,
        }}
        value={used}
        limit={limit}
      />
    </div>
  );
};

Meter.propTypes = {
  testId: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  tooltip: PropTypes.string.isRequired,
  used: PropTypes.number.isRequired,
  limit: PropTypes.number.isRequired,
};

export const TrialUsageMeters = ({
  className,
  currentCustomer,
  currentProjectId,
  predictionsLimitsData,
}) => {
  const styles = useStyles();

  // Only inside a project — the home/projects page shows the same usage in the sidebar.
  if (!currentProjectId) {
    return null;
  }

  if (!customerInTrial(currentCustomer) && !customerTrialEnded(currentCustomer)) {
    return null;
  }

  const queryQuota = getCustomerBillingQueryQuota(currentCustomer) || {};
  const planWordsUsed = queryQuota.planWordsUsed ?? 0;
  const planWordsLimit = queryQuota.planWordsLimit ?? 0;

  return (
    <div className={cx(styles.root, className)}>
      <Meter
        testId="trialUsageMeter_wordCredits"
        title="Word credits"
        tooltip={getWordCreditsUsageTooltip(planWordsLimit)}
        used={planWordsUsed}
        limit={planWordsLimit}
      />
      <Meter
        testId="trialUsageMeter_performancePredictions"
        title="Performance predictions"
        tooltip={PERFORMANCE_PREDICTIONS_USAGE_TOOLTIP}
        used={predictionsLimitsData?.currentCount ?? 0}
        limit={predictionsLimitsData?.limit ?? 0}
      />
    </div>
  );
};

TrialUsageMeters.propTypes = {
  className: PropTypes.string,
  currentCustomer: PropTypes.object,
  currentProjectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  predictionsLimitsData: PropTypes.object,
};
TrialUsageMeters.defaultProps = {
  className: '',
  currentCustomer: null,
  currentProjectId: null,
  predictionsLimitsData: null,
};
